import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; } };

export const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const emptyGap = (link) => ({ from_place: link?.last_trip?.end_place || '', to_place: '', start_at: toLocalInput(link?.last_trip?.end_time), end_at: '' });

/** {below, needsGap, diff} for a typed start reading vs the vehicle's last end reading. */
export const chainState = (link, reading) => {
  const r = Number(reading);
  if (!link || link.last_end == null || reading === '' || isNaN(r)) return { below: false, needsGap: false, diff: null };
  const diff = Math.round((r - link.last_end) * 10) / 10;
  return { below: diff < 0, needsGap: !link.returning_open && diff > link.gap_threshold_km, diff };
};

export const gapError = (g) => {
  if (!g.from_place.trim() || !g.to_place.trim()) return 'Enter where the unlogged trip started and ended';
  if (!g.start_at || !g.end_at) return 'Enter the start and end date & time of the unlogged trip';
  const s = new Date(g.start_at), e = new Date(g.end_at);
  if (e <= s) return 'The unlogged trip must end after it started';
  if (e.getTime() > Date.now() + 5 * 60000) return "The unlogged trip can't end in the future";
  return null;
};

export const gapPayload = (g) => ({ from_place: g.from_place.trim(), to_place: g.to_place.trim(), start_at: new Date(g.start_at).toISOString(), end_at: new Date(g.end_at).toISOString() });

const GapForm = ({ gap, setGap, diff }) => {
  const set = (k) => (e) => setGap((g) => ({ ...g, [k]: e.target.value }));
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="odo-gap-form">
      <p className="text-xs text-amber-900" data-testid="odo-gap-note">
        The vehicle ran <b>{diff} km</b> since its last trip ended. Enter that unlogged trip — it is saved as an <b>Unlogged run</b> for this vehicle.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[11px]">From (source)</Label><Input value={gap.from_place} onChange={set('from_place')} placeholder="e.g. Mettupatti WH" className="h-8 text-sm" data-testid="gap-from-input" /></div>
        <div><Label className="text-[11px]">To (end)</Label><Input value={gap.to_place} onChange={set('to_place')} placeholder="e.g. Driver home" className="h-8 text-sm" data-testid="gap-to-input" /></div>
        <div><Label className="text-[11px]">Started at</Label><Input type="datetime-local" value={gap.start_at} onChange={set('start_at')} className="h-8 text-sm" data-testid="gap-start-input" /></div>
        <div><Label className="text-[11px]">Ended at</Label><Input type="datetime-local" value={gap.end_at} onChange={set('end_at')} className="h-8 text-sm" data-testid="gap-end-input" /></div>
      </div>
    </div>
  );
};

/** Start-trip check: shows the vehicle's last end reading, blocks a lower start, asks for the unlogged trip on a > 5 km gap. */
export const OdoChainCheck = ({ link, reading, gap, setGap }) => {
  if (!link || link.last_end == null) return null;
  const { below, needsGap, diff } = chainState(link, reading);
  const lt = link.last_trip;
  return (
    <div className="space-y-2" data-testid="odo-chain-check">
      <p className="text-xs text-gray-600" data-testid="odo-last-end">
        Last trip on this vehicle ended at <b>{link.last_end} km</b>
        {lt?.end_place ? ` · ${lt.end_place}` : ''}{lt?.end_time ? ` · ${fmt(lt.end_time)}` : ''}
        {lt?.end_odo_photo_url && <a href={lt.end_odo_photo_url} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 underline" data-testid="odo-last-end-photo">photo</a>}
      </p>
      {below && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="odo-below-last">
          Lower than the last end reading ({link.last_end} km). Check the photo and correct the reading — the trip can't start with it. If {link.last_end} km is wrong, ask the Transport Manager to fix it in the Trip Log.
        </p>
      )}
      {needsGap && <GapForm gap={gap} setGap={setGap} diff={diff} />}
    </div>
  );
};
